/* PartsNMore: current vehicle rows, matched as make/model/year tuples. */
(() => {
  'use strict';
  const root = window, config = root.PNMFitment;
  if (!config || root.PNMFitmentAdapter) return;
  const nativeFetch = config.nativeFetch;
  const TTL = 120000, PAGE_SIZE = 200, MAX_PRODUCTS = 6000;
  const catalogs = new Map(), rows = new Map(), metadataJobs = new Map();
  let universalPromise, universalAt = 0;
  const domReady = document.readyState === 'loading' ? new Promise(resolve => document.addEventListener('DOMContentLoaded', () => setTimeout(resolve, 0), {once:true})) : Promise.resolve();
  const clone = value => JSON.parse(JSON.stringify(value));
  const brand = value => String(value || '').trim().replace(/ - ATV$/i, '').toUpperCase();
  const model = value => { const v = String(value || '').trim().toUpperCase(); return v === 'VMX12' ? 'VMX1200' : v; };
  const slug = value => String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const productId = value => String(value).replace('gid://shopify/Product/', '');
  const split = value => typeof value === 'string' ? value.replace(/\r\n?/g, '\n').trim().split('\n').map(v => v.trim()) : [];
  const jsonResponse = data => new Response(JSON.stringify(data), {headers:{'Content-Type':'application/json','Cache-Control':'no-store'}});
  function awaitSignal(promise, signal) {
    if (!signal) return promise;
    // Globo's short transport deadline does not cover a complete fitment lookup.
    // Our own requests have a deadline; user navigation still cancels immediately.
    if (signal.aborted && signal.reason !== 'timeout') return Promise.reject(new DOMException('Aborted', 'AbortError'));
    return new Promise((resolve,reject) => {
      const stop = () => { if (signal.reason !== 'timeout') reject(new DOMException('Aborted', 'AbortError')); };
      signal.addEventListener('abort', stop, {once:true});
      Promise.resolve(promise).then(resolve,reject).finally(() => signal.removeEventListener('abort',stop));
    });
  }
  async function json(url, options = {}) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 45000);
    try {
      const response = await nativeFetch(String(url), {...options, method:'GET', body:undefined, signal:controller.signal});
      if (!response.ok) throw new Error('Data request failed: ' + response.status);
      const payload=await response.json();
      return payload;
    } finally { clearTimeout(timer); }
  }
  async function parallel(items, task, width = 4) {
    const output = new Array(items.length); let next = 0;
    await Promise.all(Array.from({length:Math.min(width,items.length)}, async () => {
      while (next < items.length) { const i = next++; output[i] = await task(items[i],i); }
    }));
    return output;
  }
  function values(url, id, prefix = 'form') {
    return [...url.searchParams].filter(([key]) => key === prefix+'['+id+']' || key.startsWith(prefix+'['+id+'][')).map(([,value]) => value).filter(Boolean);
  }
  function scope(url) {
    const page = new URL(location.href);
    const tags = url.searchParams.getAll('currentTags[]');
    if (tags.some(t => /^universal$/i.test(t)) || /\/collections\/apparel(?:\/|$)/.test(page.pathname) || /\/universal(?:\+|$)/.test(page.pathname)) return null;
    const own = root.PNMCollectionYear?.context();
    let models = values(url,'705341').flatMap(v=>v.split(',')), brands = values(url,'705339').flatMap(v=>v.split(',')), years = values(url,'705342').flatMap(v=>v.split(','));
    if (!models.length && page.searchParams.has('gff_705341')) models = page.searchParams.get('gff_705341').split(',');
    if (!brands.length && page.searchParams.has('gff_705339')) brands = page.searchParams.get('gff_705339').split(',');
    if (!years.length && page.searchParams.has('gff_705342')) years = page.searchParams.get('gff_705342').split(',');
    if (!models.length && own?.bike) { models = [own.bike.modelType]; brands = [own.bike.brand]; if (own.bike.bikeYear) years = [own.bike.bikeYear]; }
    // A family collection is the union of its explicit submodels. Brand directories stay native.
    if (!models.length && own?.models?.length && own.models.length <= 40 && own.models.some(r => r.baseURL === own.base || r.target === own.base)) {
      models = own.models.map(r => r.modelType); brands = own.models.map(r => r.brand);
    }
    if (!models.length || !brands.length) return null;
    const pairs = new Set();
    // With explicit filters, every requested make/model pair still requires a real row.
    brands.forEach(b => models.forEach(m => pairs.add(brand(b)+'|'+model(m))));
    return {models:[...new Set(models)], brands:[...new Set(brands)], years:new Set(years.filter(y => y !== '*')), pairs,
      key:[...pairs].sort().join(',')+'|'+years.slice().sort().join(',')};
  }
  function parseRow(row) {
    let b = split(row.brands); const m = split(row.models), y = split(row.years);
    // Repeated identical makes are unambiguous when model/year rows align.
    // The catalog's 19-0061 fuel pump has six "Honda" labels for its two rows.
    if(b.length!==m.length && m.length===y.length && b[0] && new Set(b.map(brand)).size===1) b=Array(m.length).fill(b[0]);
    if (!b.length || b.length !== m.length || m.length !== y.length || b.some((v,i) => !v || !m[i] || !y[i])) return null;
    return b.map((v,i) => [brand(v)+'|'+model(m[i]),y[i]]);
  }
  function matches(row, selected) {
    const tuples = parseRow(row);
    return !!tuples && tuples.some(([pair,year]) => selected.pairs.has(pair) && /^\d{4}$/.test(year) && (!selected.years.size || selected.years.has(year)));
  }
  function dataURL(query, page = 1) {
    const url = new URL(config.searchURL,location.origin);
    url.searchParams.set('view','pnm-fitment-data'); url.searchParams.set('type','product');
    url.searchParams.set('q',query); url.searchParams.set('page',String(page));
    url.searchParams.set('options[unavailable_products]','show');
    url.searchParams.set('pnm_revision',String(Math.floor(Date.now()/TTL)));
    return url;
  }
  async function dataPage(query, page) {
    const data = await json(dataURL(query,page), {credentials:'same-origin'});
    if (data.version !== 1 || String(data.theme) !== String(config.themeId) || !Array.isArray(data.products) || data.page !== page || data.pages < 0 || data.pages > 30) throw new Error('Fitment data could not be verified');
    data.products.forEach(row => { if (!/^\d+$/.test(productId(row.id)) || typeof row.handle !== 'string') throw new Error('Invalid product identity'); });
    return data;
  }
  async function dataQuery(query) {
    const first = await dataPage(query,1);
    const rest = await parallel(Array.from({length:Math.max(0,first.pages-1)},(_,i) => i+2), page => dataPage(query,page));
    if (rest.some(p => p.pages !== first.pages || p.count !== first.count)) throw new Error('Fitment search changed while loading');
    const result = [first,...rest].flatMap(p => p.products);
    if (new Set(result.map(p => productId(p.id))).size !== result.length) throw new Error('Duplicate fitment search page');
    result.forEach(row => rows.set(productId(row.id),{at:Date.now(),row}));
    return result;
  }
  async function universalRows() {
    if (!universalPromise || Date.now()-universalAt > TTL) {
      universalAt = Date.now();
      try {
        const saved=JSON.parse(sessionStorage.getItem('pnm_universal_fitment_v1')||'null');
        if(saved && Date.now()-saved.at<TTL && String(saved.theme)===String(config.themeId) && Array.isArray(saved.rows)) {
          universalAt=saved.at;universalPromise=Promise.resolve(saved.rows);return universalPromise;
        }
      } catch(_) {}
      universalPromise = (async () => {
        async function page(n) {
          const url = new URL(config.universalURL,location.origin);
          url.searchParams.set('view','pnm-fitment-universal');url.searchParams.set('page',String(n));
          url.searchParams.set('pnm_revision',String(Math.floor(Date.now()/TTL)));
          const data = await json(url,{credentials:'same-origin'});
          if (data.version!==1 || String(data.theme)!==String(config.themeId) || !Array.isArray(data.products) || data.page!==n || data.pages<0 || data.pages>30) throw new Error('Universal fitment data could not be verified');
          return data;
        }
        const first = await page(1), rest = await parallel(Array.from({length:Math.max(0,first.pages-1)},(_,i)=>i+2),page);
        if(rest.some(p=>p.pages!==first.pages||p.count!==first.count)) throw new Error('Universal collection changed while loading');
        const result = [first,...rest].flatMap(p=>p.products);
        result.forEach(row=>rows.set(productId(row.id),{at:Date.now(),row}));
        try {sessionStorage.setItem('pnm_universal_fitment_v1',JSON.stringify({at:Date.now(),theme:config.themeId,rows:result}));}catch(_){}
        return result;
      })().catch(error => { universalPromise = null; throw error; });
    }
    return universalPromise;
  }
  async function loadMetadata(products) {
    const missing = products.filter(p => !rows.has(productId(p.id)) || Date.now()-rows.get(productId(p.id)).at > TTL);
    const skus = [...new Set(missing.flatMap(p => (p.variants || []).map(v => v.sku).filter(v => typeof v === 'string' && v)))];
    const batches = [];
    for (let i=0;i<skus.length;i+=100) batches.push(skus.slice(i,i+100));
    await parallel(batches, batch => dataQuery(batch.map(s => 'variants.sku:"'+s.replace(/\\/g,'\\\\').replace(/"/g,'\\"')+'"').join(' OR ')));
    // Search may omit hidden-search products. Fetch those exact public handles directly.
    const remaining = missing.filter(p => !rows.has(productId(p.id)) || Date.now()-rows.get(productId(p.id)).at > TTL);
    const fallback = [];
    for (let i=0;i<remaining.length;i+=20) fallback.push(remaining.slice(i,i+20));
    await parallel(fallback, batch => dataQuery('pnm-handles|'+batch.map(p => p.handle).join('|')));
    const result = new Map();
    products.forEach(product => {
      const item = rows.get(productId(product.id));
      if (!item || item.row.handle !== product.handle || Date.now()-item.at > TTL) throw new Error('A product fitment record is unavailable');
      result.set(productId(product.id),item.row);
    });
    return result;
  }
  function metadata(products) {
    const key = products.map(p=>productId(p.id)).sort().join(',');
    if (metadataJobs.has(key)) return metadataJobs.get(key);
    const promise = loadMetadata(products);
    metadataJobs.set(key,promise);
    promise.then(()=>metadataJobs.delete(key),()=>metadataJobs.delete(key));
    return promise;
  }
  function categoryNames(template) {
    const names = new Set((template.filters || []).find(f => String(f.id)==='852978')?.values?.map(v => v.value) || []);
    document.querySelectorAll('[data-pnm-main-categories] a[data-category]').forEach(link => {
      if (link.dataset.pnmApparelCategory !== 'true') names.add(link.dataset.category === 'Tach & Speedo' ? 'Tachometer & Speedometer' : link.dataset.category);
    });
    names.add('Tachometer & Speedometer');
    return [...names].sort();
  }
  function sourceRequest(url, selected, includeUniversal) {
    const request = new URL(url), categories = new Set();
    [...request.searchParams].forEach(([key,value]) => {
      if (/^(?:form|filter)\[(?:852978|category)\]/.test(key)) { categories.add(value); request.searchParams.delete(key); }
      if (/^(?:form|filter)\[(?:705339|705340|705341|705342|852962|select-your-model)\]/.test(key)) request.searchParams.delete(key);
    });
    const nav = document.querySelector('[data-pnm-main-categories]');
    if (Number(url.searchParams.get('limit'))!==1 && nav?.dataset.categoryPage === 'true' && nav.dataset.activeCategory && nav.dataset.activeCategory !== 'Apparel') categories.add(nav.dataset.activeCategory === 'Tach & Speedo' ? 'Tachometer & Speedometer' : nav.dataset.activeCategory);
    const names=new Set(categoryNames({filters:[]}));
    request.searchParams.getAll('currentTags[]').forEach(tag=>{if(names.has(tag))categories.add(tag);});
    const partType = new URL(location.href).searchParams.get('pnm_part_type');
    const retained = request.searchParams.getAll('currentTags[]').filter(t => partType && slug(t)===partType);
    request.searchParams.delete('currentTags[]'); request.searchParams.delete('currentTags');
    retained.forEach(t => request.searchParams.append('currentTags[]',t));
    request.searchParams.set('collection','0');
    // Brand/series/year arrays can create cross-products; model is only a candidate source.
    const models = new Set(selected.models);
    if (selected.models.some(m => model(m)==='VMX1200')) {models.add('VMX12');models.add('VMX1200');}
    models.forEach(m => request.searchParams.append('form[705341][]',m));
    if (!includeUniversal) request.searchParams.append('excludeTags[]','Universal');
    ['page','limit','event','ncp','cid','did'].forEach(k => request.searchParams.delete(k));
    request.searchParams.sort();
    return {url:request,categories};
  }
  async function completeCatalog(url) {
    const key = String(url), hit = catalogs.get(key);
    if (hit && Date.now()-hit.at < TTL) return hit.promise;
    const promise = (async () => {
      async function page(n) {
        const request = new URL(url); request.searchParams.set('page',String(n));request.searchParams.set('limit',String(PAGE_SIZE));request.searchParams.set('event','init');
        const data = await json(request,{credentials:'omit'}), p = data.pagination;
        if (!Array.isArray(data.products) || data.processing || !p || Number(p.current_page)!==n || Number(p.per_page)!==PAGE_SIZE || !Number.isInteger(Number(p.total)) || Number(p.total)<0 || Number(p.total)>MAX_PRODUCTS || Number(p.last_page)!==Math.ceil(Number(p.total)/PAGE_SIZE)) throw new Error('Catalog pagination is incomplete');
        return data;
      }
      const first = await page(1), count = Number(first.pagination.total), pages = Math.ceil(count/PAGE_SIZE);
      const rest = await parallel(Array.from({length:Math.max(0,pages-1)},(_,i) => i+2),page);
      const products = [];
      [first,...rest].forEach((data,i) => {
        if (Number(data.pagination.total)!==count || data.products.length!==Math.max(0,Math.min(PAGE_SIZE,count-i*PAGE_SIZE)) || !!data.pagination.hasMorePages!==(i+1<pages)) throw new Error('Catalog changed while loading');
        products.push(...data.products);
      });
      if (new Set(products.map(p=>productId(p.id))).size!==count) throw new Error('Incomplete catalog pages');
      return {template:first,products};
    })();
    catalogs.set(key,{at:Date.now(),promise});
    while(catalogs.size>6) catalogs.delete(catalogs.keys().next().value);
    promise.catch(() => catalogs.delete(key));
    return promise;
  }
  function emit(status, detail = {}) {
    root.dispatchEvent(new CustomEvent('pnm:fitment-status',{detail:{status,...detail}}));
  }
  async function result(url, selected) {
    const baseRequest=sourceRequest(url,selected,false);
    const basePromise=completeCatalog(baseRequest.url).then(async catalog=>({catalog,records:await metadata(catalog.products)}));
    const [universal,base]=await Promise.all([universalRows(),basePromise]);
    const extras = universal.filter(row => matches(row,selected));
    const request = extras.length ? sourceRequest(url,selected,true) : baseRequest;
    const catalog = extras.length ? await completeCatalog(request.url) : base.catalog;
    const extraIds = new Set(extras.map(row => productId(row.id)));
    const candidates = catalog.products.filter(p => !p.tags.includes('Apparel') && (!p.tags.includes('Universal') || extraIds.has(productId(p.id))));
    const records = extras.length ? await metadata(candidates) : base.records;
    const invalidIds = candidates.filter(p=>!parseRow(records.get(productId(p.id)))).map(p=>productId(p.id));
    const fitted = candidates.filter(p => matches(records.get(productId(p.id)),selected));
    const payload = clone(catalog.template), names = categoryNames(payload);
    const original = (payload.filters || []).find(f=>String(f.id)==='852978');
    const category = original || {id:852978,handle:'category',attribute:'Tag',label:'Category',style:'Checkbox',class:' gf-option-852978 gf-option-block-checkbox',show_on_sidebar:true,show_on_toolbar:false};
    category.values = names.map(value => ({value,label:value,count:fitted.filter(p=>p.tags.includes(value)).length,...(request.categories.has(value)?{selected:true}:{})}));
    request.categories.forEach(value => {if (!names.includes(value)) category.values.push({value,label:value,count:0,selected:true});});
    category.is_selected = request.categories.size>0; category.expansion_mode=3;
    payload.filters = [category,...(payload.filters || []).filter(f=>String(f.id)!=='852978')];
    const matched = fitted.filter(p=>!request.categories.size || [...request.categories].some(c=>p.tags.includes(c)));
    const page = Math.max(1,Number(url.searchParams.get('page'))||1), limit = Math.min(200,Math.max(1,Number(url.searchParams.get('limit'))||36));
    if (!Number.isInteger(page) || !Number.isInteger(limit)) throw new Error('Invalid pagination');
    const start = (page-1)*limit;
    payload.products = clone(matched.slice(start,start+limit));payload.products.forEach(p=>{p.page=page;});
    payload.pagination = {from:payload.products.length?start+1:0,to:payload.products.length?start+payload.products.length:0,total:matched.length,per_page:limit,hasMorePages:start+limit<matched.length,current_page:page,last_page:Math.ceil(matched.length/limit)};
    payload.has_filter=true;payload.suggestion_products=[];delete payload.processing;
    if (payload.search_tabs?.products) payload.search_tabs.products.count=matched.length;
    return {payload,invalidIds,sourceTotal:catalog.products.length};
  }
  async function handle(input, options = {}) {
    await domReady;
    const url = new URL(typeof input==='string' || input instanceof URL ? input : input.url,location.href);
    const selected = scope(url), method = options.method || input?.method || 'GET';
    if (!selected || String(method).toUpperCase()!=='GET') return nativeFetch(input,options);
    const signal = options.signal || input?.signal;
    const updateUI = Number(url.searchParams.get('limit')) !== 1;
    if (updateUI) emit('loading',{key:selected.key});
    try {
      const data = await awaitSignal(result(url,selected),signal);
      if (updateUI) emit('ready',{key:selected.key,total:data.payload.pagination.total,invalidIds:data.invalidIds,sourceTotal:data.sourceTotal});
      return jsonResponse(data.payload);
    } catch(error) {
      if (error.name==='AbortError' && signal?.aborted && signal.reason!=='timeout') throw error;
      console.warn('[PNM fitment]', error.message);
      if (!updateUI) throw error;
      emit('error',{key:selected.key,reason:error.message});
      // Never silently display an unverified or partial collection after a failed lookup.
      return jsonResponse({filters:[],form:[],products:[],suggestion_products:[],has_filter:true,pagination:{from:0,to:0,total:0,per_page:36,hasMorePages:false,current_page:1,last_page:0}});
    }
  }
  let state;
  function paint() {
    const grid = document.getElementById('CollectionProductGrid');
    if (!grid || !state) return;
    let node = document.getElementById('pnm-fitment-status');
    if (!node) {node=document.createElement('p');node.id='pnm-fitment-status';node.setAttribute('role','status');node.style.cssText='padding:12px 16px;margin:12px 0;border-radius:6px;background:#fff4e5;color:#292929;font-size:16px';grid.prepend(node);}
    node.hidden=state.status==='ready';node.textContent=state.status==='error'?config.error:config.loading;
    node.dataset.status=state.status;grid.dataset.pnmFitmentStatus=state.status;
  }
  root.addEventListener('pnm:fitment-status',event=>{state=event.detail;paint();});
  root.addEventListener('globoFilterRenderCompleted',paint);
  function checkProduct(id,bike) {
    const key=String(id||'').replace(/^product-/,'');
    const entry=rows.get(key);
    if(!entry || !bike?.brand || !bike?.model || !/^\d{4}$/.test(bike.year||'')) return null;
    return matches(entry.row,{pairs:new Set([brand(bike.brand)+'|'+model(bike.model)]),years:new Set([bike.year])});
  }
  root.PNMFitmentAdapter = {version:'1.0.0',handle,matches,parseRow,scope,sourceRequest,checkProduct,clearCache(){catalogs.clear();rows.clear();universalPromise=null;},get status(){return state;}};
  config.ready(root.PNMFitmentAdapter);
})();
